'use client';

import React, { forwardRef, useEffect, useId, useState } from 'react';
import classNames from 'classnames';
import { Caption } from '@/design-system/components/Basic/Typography';
import { formatDdMmYyyyInput } from '@/utils/helpers';
import { format, isValid, parse } from 'date-fns';
import { useTranslate } from '@tolgee/react';
import { twMerge } from 'tailwind-merge';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const DATE_PLACEHOLDER_EN = 'DD.MM.YYYY';
const DATE_PLACEHOLDER_GER = 'TT.MM.JJJJ';

export interface DateInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'onError'
> {
  label?: string;
  error?: string;
  /** Value as Date; undefined or null for empty */
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  onError?: (error: string | null) => void;
  className?: string;
  inputClassName?: string;
  suppressBuiltInError?: boolean;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      label,
      error,
      value,
      onChange,
      suppressBuiltInError,
      onError,
      className,
      inputClassName,
      id,
      required,
      disabled,
      onBlur,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `date-input-${generatedId.replace(/:/g, '-')}`;
    const [dateValue, setDateValue] = useState(() => (value ? format(value, 'dd.MM.yyyy') : ''));
    const [errorMessage, setErrorMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const { t } = useTranslate();
    const locale = useSelectedLanguage();
    const DATE_PLACEHOLDER = locale === 'en' ? DATE_PLACEHOLDER_EN : DATE_PLACEHOLDER_GER;
    const selectionRef = React.useRef<{ start: number; end: number } | null>(null);

    const handleBeforeInput = (e: React.SyntheticEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      selectionRef.current = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      };
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const inputType = (e.nativeEvent as InputEvent).inputType;
      if (hasError) {
        setHasError(false);
        setErrorMessage('');
      }
      const prev = selectionRef.current;
      const hadSelection = prev != null && prev.start !== prev.end;
      const cursorAtEnd = prev != null && prev.start === value.length - 1;
      if (hadSelection || !cursorAtEnd) {
        setDateValue(value);
        selectionRef.current = null;
        return;
      }

      if (inputType?.startsWith('delete')) {
        setDateValue(value);
        return;
      }
      const formatted = formatDdMmYyyyInput(value);
      setDateValue(formatted);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (dateValue.trim() === '') {
        setHasError(false);
        setErrorMessage('');
        onError?.(null);
        return;
      }

      const parsedDate = parse(dateValue, 'dd.MM.yyyy', new Date());
      const isValidDate = isValid(parsedDate);
      if (!isValidDate) {
        setHasError(true);
        setErrorMessage(t('common.invalid_date'));
        onError?.(t('common.invalid_date'));
        return;
      }
      onChange?.(parsedDate ?? null);
      onBlur?.(e);
      onError?.(null);
    };

    useEffect(() => {
      if (!value) {
        setDateValue('');
        return;
      }
      setDateValue(format(value, 'dd.MM.yyyy'));
    }, [value]);

    const remainingPlaceholder = DATE_PLACEHOLDER.slice(dateValue.length);

    return (
      <div
        className={twMerge(
          classNames('ds-input-wrapper flex flex-col gap-3 min-h-25 max-w-ds-40', className),
        )}
      >
        {label && (
          <Caption as="label" htmlFor={inputId} className="text-ds-text-3">
            {label}
            {required && ' *'}
          </Caption>
        )}
        <div className="relative container-gradient-border rounded-xl">
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={!!error || !!hasError}
            aria-describedby={error || hasError ? `${inputId}-error` : undefined}
            placeholder=" "
            value={dateValue}
            onBeforeInput={handleBeforeInput}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={10}
            className={classNames(
              'ds-input-trigger-base input-border-gradient min-h-ds-11 px-3.5 py-2.5 caret-ds-text-1',
              inputClassName,
            )}
            {...rest}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden px-3.5 py-2.5 text-ds-text-s flex items-center"
            style={{
              opacity: disabled ? 0.9 : undefined,
            }}
          >
            <span className="invisible">{dateValue}</span>
            <span className="text-ds-grey-600">{remainingPlaceholder}</span>
          </div>
        </div>
        {!suppressBuiltInError && (error || errorMessage) && (
          <Caption as="p" id={`${inputId}-error`} className="text-ds-text-feedback-error">
            {error || errorMessage}
          </Caption>
        )}
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';

export default DateInput;
